/**
 * One launcher row — a place the browser can open.
 *
 * @module BrowserLauncherRow
 */
import { GlobeIcon, RadioTowerIcon, XIcon } from "lucide-react";

import { formatRelativeTimeLabel } from "~/timestampFormat";

import type { BrowserLauncherTarget } from "./browserLauncherLogic";

export interface BrowserLauncherRowProps {
  readonly target: BrowserLauncherTarget;
  readonly onOpen: () => void;
  /** Only recents are removable; live rows disappear when the server stops. */
  readonly onRemove?: (() => void) | undefined;
}

export function BrowserLauncherRow(props: BrowserLauncherRowProps) {
  const Icon = props.target.kind === "dev-server" ? RadioTowerIcon : GlobeIcon;
  const detail = rowDetail(props.target);

  return (
    <div className="group/launcher-row relative flex w-full items-center">
      <button
        type="button"
        className="flex w-full items-center gap-3 px-3 py-2.5 pr-10 text-left transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring"
        onClick={props.onOpen}
      >
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-border/70 bg-background text-muted-foreground">
          <Icon className="size-3.5" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium text-foreground">
            {props.target.title ?? props.target.label}
          </span>
          {detail === null ? null : (
            <span className="truncate text-xs text-muted-foreground">{detail}</span>
          )}
        </span>
      </button>
      {props.onRemove === undefined ? null : (
        <button
          type="button"
          className="absolute right-2 inline-flex size-6 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring group-hover/launcher-row:opacity-100"
          aria-label={`Remove ${props.target.label} from history`}
          onClick={props.onRemove}
        >
          <XIcon className="size-3.5" />
        </button>
      )}
    </div>
  );
}

/**
 * The second line.
 *
 * When a title took the first line the URL still has to be visible somewhere —
 * a row that shows only "Vite + React" tells the user nothing about which port
 * it will open. So the label leads, and the visit time is appended to it rather
 * than replacing it.
 */
function rowDetail(target: BrowserLauncherTarget): string | null {
  const visited =
    target.lastVisitedAt === null
      ? null
      : formatRelativeTimeLabel(new Date(target.lastVisitedAt).toISOString());
  const parts = [target.title === null ? null : target.label, target.detail, visited].filter(
    (part): part is string => part !== null && part !== "",
  );
  return parts.length === 0 ? null : parts.join(" · ");
}
